import { useMemo, useState } from 'react';

import {
  DentistItemSlot,
  DialogState,
  ExtractionFillTrace,
  LastExtractionTrace,
  LevelAggregate,
} from '../types';

interface Timing {
  total_ms?: number;
  extraction_ms?: number;
  nlg_ms?: number;
  tts_lipsync_ms?: number;
}

interface DentistFSMPanelProps {
  fsmState: DialogState | null;
  timing: Timing | null;
  targetCategories?: string[];
}

interface SelectedItemRef {
  categoryId: string;
  itemKey: string;
}

interface SelectedItemDetail {
  categoryId: string;
  categoryName: string;
  item: DentistItemSlot;
  aggregate?: LevelAggregate;
}

const PHASE_LABELS: Record<string, { label: string; color: string }> = {
  greeting:    { label: '👋 挨拶',     color: 'bg-purple-100 text-purple-800' },
  listening:   { label: '📥 聴取中',   color: 'bg-blue-100 text-blue-800' },
  confirming:  { label: '🔍 確認中',   color: 'bg-yellow-100 text-yellow-800' },
  summarizing: { label: '📝 まとめ中', color: 'bg-orange-100 text-orange-800' },
  done:        { label: '✅ 完了',     color: 'bg-green-100 text-green-800' },
};

const SUB_PHASE_LABELS: Record<string, string> = {
  prompting: '質問中',
  listening: '回答待ち',
  coaching:  'コーチング',
};

const DEFAULT_LEVEL_KEYS = ['Lv1', 'Lv2', 'Lv3', 'Lv4', 'Lv5', 'Lv6'];

const SPECIAL_LEVEL_ORDER: Record<string, number> = {
  proposal: 500,
  acceptance_intent: 600,
};

const KIND_LABELS: Record<string, string> = {
  fact: '事実',
  measurement: '数量',
  method: '方法',
  reason: '背景',
  goal: '提案',
  commitment: '受容',
  legacy: '旧Lv',
};

const MATCHED_BY_LABELS: Record<string, string> = {
  item_key: 'item_key',
  column_key: 'column_key',
  content: 'content',
  dimension: 'dimension',
  kind: 'kind',
  required_order: 'required_order fallback',
  any_order: 'any_order fallback',
};

const SKIPPED_REASON_LABELS: Record<string, string> = {
  coached_category: 'coached category',
  unknown_category: 'unknown category',
  unknown_level: 'unknown level',
  no_matching_slot: 'no matching slot',
  already_filled_or_na: 'already filled / N/A',
};

function isReportedItem(slot: DentistItemSlot): boolean {
  return slot.filled && Boolean(slot.value) && slot.value !== 'N/A';
}

function levelStatusLabel(status: string): string {
  switch (status) {
    case 'na':
      return '対象外';
    case 'filled':
      return '充填';
    case 'partial':
      return '部分取得';
    case 'empty':
      return '未取得';
    default:
      return status || '未取得';
  }
}

function getLevelStatus(
  fsmState: DialogState,
  categoryId: string,
  lvKey: string,
  fallbackFilled: boolean,
  fallbackNA: boolean,
): string {
  return fsmState.slots?.[categoryId]?.levelStatuses?.[lvKey]
    ?? fsmState.levelAggregates?.[categoryId]?.[lvKey]?.status
    ?? (fallbackNA ? 'na' : fallbackFilled ? 'filled' : 'empty');
}

function getLevelItems(fsmState: DialogState, categoryId: string, lvKey: string): DentistItemSlot[] {
  return Object.values(fsmState.itemSlots?.[categoryId] ?? {})
    .filter((item) => item.level === lvKey && item.value !== 'N/A');
}

function levelKeySortValue(levelKey: string): number {
  const match = /^Lv(\d+)/.exec(levelKey);
  return match ? Number(match[1]) : (SPECIAL_LEVEL_ORDER[levelKey] ?? 999);
}

function sortLevelKeys(levelKeys: Iterable<string>): string[] {
  return Array.from(new Set(levelKeys)).sort((a, b) => {
    const numericDiff = levelKeySortValue(a) - levelKeySortValue(b);
    if (numericDiff !== 0) return numericDiff;
    return a.localeCompare(b);
  });
}

function getCategoryLevelKeys(fsmState: DialogState, categoryId: string): string[] {
  const slot = fsmState.slots?.[categoryId];
  const keys = new Set<string>();

  Object.keys(fsmState.levelAggregates?.[categoryId] ?? {}).forEach((key) => keys.add(key));
  Object.values(fsmState.itemSlots?.[categoryId] ?? {}).forEach((item) => keys.add(item.level));
  Object.keys(slot?.filledLevels ?? {}).forEach((key) => keys.add(key));
  Object.keys(slot?.levelStatuses ?? {}).forEach((key) => keys.add(key));
  Object.keys(fsmState.rubricDescriptions?.[categoryId] ?? {}).forEach((key) => keys.add(key));
  Object.keys(fsmState.tableValues?.[categoryId] ?? {}).forEach((key) => keys.add(key));
  (fsmState.naLevelMap?.[categoryId] ?? []).forEach((key) => keys.add(key));

  return keys.size > 0 ? sortLevelKeys(keys) : DEFAULT_LEVEL_KEYS;
}

function summarizeCategoryProgress(fsmState: DialogState, categoryId: string): { filled: number; total: number } {
  const items = Object.values(fsmState.itemSlots?.[categoryId] ?? {})
    .filter((item) => item.value !== 'N/A');
  if (items.length > 0) {
    return {
      filled: items.filter(isReportedItem).length,
      total: items.length,
    };
  }

  const levelKeys = getCategoryLevelKeys(fsmState, categoryId);
  const slot = fsmState.slots?.[categoryId];
  const naLevels = fsmState.naLevelMap?.[categoryId] ?? [];
  const isCategoryNA = (fsmState.naCategories ?? []).includes(categoryId);
  const activeLevelKeys = levelKeys.filter((levelKey) => !isCategoryNA && !naLevels.includes(levelKey));

  return {
    filled: activeLevelKeys.filter((levelKey) => (slot?.filledLevels?.[levelKey] ?? '') !== '').length,
    total: activeLevelKeys.length,
  };
}

function itemStatusLabel(item: DentistItemSlot): string {
  if (item.value === 'N/A') return '対象外';
  if (isReportedItem(item)) return item.wasProbed ? 'お助け後に取得' : '自発取得';
  return '未取得';
}

function itemStatusClass(item: DentistItemSlot): string {
  if (item.value === 'N/A') return 'bg-gray-100 text-gray-600 border-gray-200';
  if (isReportedItem(item)) {
    return item.wasProbed
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-cyan-50 text-cyan-700 border-cyan-200';
  }
  return 'bg-gray-50 text-gray-600 border-gray-200';
}

function itemDotClass(item: DentistItemSlot): string {
  if (item.value === 'N/A') return 'border-gray-400 bg-gray-800 text-white';
  if (isReportedItem(item)) {
    if (item.wasProbed) return 'border-orange-300 bg-orange-400 text-white';
    if (item.kind === 'goal') return 'border-emerald-300 bg-emerald-500 text-white';
    if (item.kind === 'commitment') return 'border-purple-300 bg-purple-500 text-white';
    return 'border-blue-300 bg-blue-500 text-white';
  }
  if (item.reviewRequired) return 'border-rose-300 bg-rose-50 text-rose-700';
  if (item.kind === 'goal') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (item.kind === 'commitment') return 'border-purple-200 bg-purple-50 text-purple-700';
  return 'border-gray-300 bg-gray-100 text-gray-600';
}

function extractMarker(value: string | undefined): string | null {
  const marker = value?.match(/[①-⑳㉑-㉟㊱-㊿]/)?.[0];
  if (marker) return marker;
  const suffix = value?.match(/_(\d{2})$/)?.[1];
  return suffix ? String(Number(suffix)) : null;
}

function itemChipLabel(item: DentistItemSlot, index: number): string {
  return extractMarker(item.label)
    ?? extractMarker(item.columnKey)
    ?? ((item.dimension === 'hygienist_proposal' ? '提' : item.dimension === 'patient_acceptance' ? '受' : '')
      || String(index + 1));
}

function levelDisplayLabel(levelKey: string, items: DentistItemSlot[]): string {
  const kind = items.find((item) => item.kind)?.kind;
  if (kind && KIND_LABELS[kind]) return KIND_LABELS[kind];
  if (levelKey === 'proposal') return '提案';
  if (levelKey === 'acceptance_intent') return '受容';
  return levelKey;
}

function itemDisplayName(item: DentistItemSlot): string {
  return item.label || item.sourceHeader || item.columnKey || item.itemKey;
}

function ItemSlotHoverTooltip({
  categoryId,
  levelLabel,
  levelStatus,
  desc,
  tableVal,
  item,
  notes,
  align = 'left',
}: {
  categoryId: string;
  levelLabel: string;
  levelStatus: string;
  desc?: string;
  tableVal?: string;
  item: DentistItemSlot;
  notes?: string[];
  align?: 'left' | 'center';
}) {
  const positionClass = align === 'center'
    ? 'right-3 top-24'
    : 'right-3 top-20';
  const reported = isReportedItem(item);

  return (
    <div className={`fixed ${positionClass} hidden group-hover:block z-[9999] pointer-events-none`}>
      <div className="max-h-[60vh] w-[380px] max-w-[calc(100vw-24px)] overflow-y-auto rounded bg-gray-900 px-2 py-1 text-xs text-white shadow-lg">
        <div className="font-semibold">
          {categoryId} {levelLabel} / {itemDisplayName(item)} [{levelStatus}]
        </div>
        <div className="font-mono text-[10px] text-gray-400 break-all">{item.itemKey}</div>
        <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] text-gray-300">
          <span>kind: {item.kind ?? '-'}</span>
          <span>dimension: {item.dimension || '-'}</span>
          {item.pairKey && <span className="text-emerald-300">pair: {item.pairKey}</span>}
          {item.groupKey && <span className="text-emerald-300">group: {item.groupKey}</span>}
        </div>
        {desc && <div className="mt-1 whitespace-normal text-gray-300">{desc}</div>}
        {tableVal && <div className="mt-1 whitespace-normal text-cyan-300">条件: {tableVal}</div>}
        <div className="mt-1 border-t border-gray-700 pt-1 whitespace-normal">
          <div className={reported ? 'text-blue-300' : 'text-gray-300'}>
            <span className="font-semibold">{itemStatusLabel(item)}</span>
            <span> / 期待値: </span>
            <span>{item.text || '(空)'}</span>
          </div>
          {reported && item.value && <div className="text-blue-300">取得値: {item.value}</div>}
          {item.sourceText && <div className="text-gray-300">根拠: {item.sourceText}</div>}
          {item.reviewRequired && <div className="text-rose-300">reviewRequired</div>}
        </div>
        {notes && notes.length > 0 && (
          <div className="mt-1 border-t border-gray-600 pt-1 whitespace-normal text-yellow-300">
            {notes.join(' / ')}
          </div>
        )}
      </div>
    </div>
  );
}

function isV31SourceCategoryId(categoryId: string): boolean {
  return /^SRC_C\d+$/.test(categoryId);
}

function canonicalCategoryId(categoryId: string): string {
  return categoryId.replace(/^SRC_/, '');
}

function buildTargetCategorySet(targetCategories?: string[]): Set<string> | null {
  if (!targetCategories || targetCategories.length === 0) return null;
  const ids = new Set<string>();
  for (const categoryId of targetCategories) {
    ids.add(categoryId);
    ids.add(canonicalCategoryId(categoryId));
  }
  return ids;
}

function isInstructionTargetCategory(categoryId: string, targetCategorySet: Set<string> | null): boolean {
  if (!targetCategorySet || categoryId === 'C0') return false;
  return targetCategorySet.has(categoryId) || targetCategorySet.has(canonicalCategoryId(categoryId));
}

function summarizeKinds(
  fsmState: DialogState,
  visibleCategoryIds?: Set<string>,
): Array<{ key: string; label: string; filled: number; total: number }> {
  const summary = new Map<string, { key: string; label: string; filled: number; total: number }>();
  for (const [categoryId, categoryItems] of Object.entries(fsmState.itemSlots ?? {})) {
    if (visibleCategoryIds && !visibleCategoryIds.has(categoryId)) continue;
    for (const item of Object.values(categoryItems)) {
      if (item.value === 'N/A') continue;
      const key = item.kind ?? 'legacy';
      const entry = summary.get(key) ?? { key, label: KIND_LABELS[key] ?? key, filled: 0, total: 0 };
      entry.total++;
      if (isReportedItem(item)) entry.filled++;
      summary.set(key, entry);
    }
  }
  const order = ['fact', 'measurement', 'method', 'reason', 'goal', 'commitment', 'legacy'];
  return Array.from(summary.values()).sort((a, b) => {
    const leftOrder = order.indexOf(a.key);
    const rightOrder = order.indexOf(b.key);
    return (leftOrder === -1 ? 99 : leftOrder) - (rightOrder === -1 ? 99 : rightOrder);
  });
}

function summarizePairs(
  fsmState: DialogState,
  visibleCategoryIds?: Set<string>,
): { filled: number; total: number } {
  const pairs = new Map<string, { total: number; filled: number }>();
  for (const [categoryId, categoryItems] of Object.entries(fsmState.itemSlots ?? {})) {
    if (visibleCategoryIds && !visibleCategoryIds.has(categoryId)) continue;
    for (const item of Object.values(categoryItems)) {
      if (!item.pairKey || item.value === 'N/A') continue;
      const pair = pairs.get(item.pairKey) ?? { total: 0, filled: 0 };
      pair.total++;
      if (isReportedItem(item)) pair.filled++;
      pairs.set(item.pairKey, pair);
    }
  }
  let total = 0;
  let filled = 0;
  for (const pair of Array.from(pairs.values())) {
    total++;
    if (pair.total > 0 && pair.filled === pair.total) filled++;
  }
  return { filled, total };
}

function formatDebugValue(value: unknown, emptyLabel = '未取得'): string {
  if (value === null || value === undefined || value === '') return emptyLabel;
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : value.toFixed(2);
  return String(value);
}

function formatJsonValue(value: unknown): string {
  if (value === undefined) return '未送出';
  try {
    return JSON.stringify(value, null, 2) ?? String(value);
  } catch {
    return String(value);
  }
}

function findTraceForItem(
  lastExtraction: LastExtractionTrace | undefined,
  categoryId: string,
  item: DentistItemSlot,
): ExtractionFillTrace | undefined {
  return lastExtraction?.fills.find((fill) => {
    return fill.categoryId === categoryId
      && (
        fill.itemKey === item.itemKey
        || fill.rawItem.item_key === item.itemKey
        || (
          fill.level === item.level
          && fill.rawItem.column_key === item.columnKey
          && fill.rawItem.dimension === item.dimension
        )
      );
  });
}

function traceResultLabel(trace: ExtractionFillTrace): string {
  if (trace.matchedBy) {
    return MATCHED_BY_LABELS[trace.matchedBy] ?? trace.matchedBy;
  }
  if (trace.skippedReason) {
    return SKIPPED_REASON_LABELS[trace.skippedReason] ?? trace.skippedReason;
  }
  return 'matched_by 未送出';
}

function traceStatusClass(trace: ExtractionFillTrace): string {
  if (trace.filled) return 'border-cyan-200 bg-cyan-50 text-cyan-700';
  if (trace.skippedReason === 'already_filled_or_na') return 'border-gray-200 bg-gray-50 text-gray-600';
  return 'border-rose-200 bg-rose-50 text-rose-700';
}

function DetailRow({ label, value, multiline = false }: { label: string; value: string; multiline?: boolean }) {
  return (
    <div className="grid grid-cols-[112px_1fr] gap-2 border-t border-gray-100 py-1.5">
      <dt className="text-gray-500">{label}</dt>
      <dd className={`text-gray-900 ${multiline ? 'whitespace-pre-wrap break-words' : 'truncate'}`}>
        {value}
      </dd>
    </div>
  );
}

function ItemSlotDetailPanel({
  selectedItem,
  lastExtraction,
  onClose,
}: {
  selectedItem: SelectedItemDetail | null;
  lastExtraction?: LastExtractionTrace;
  onClose: () => void;
}) {
  if (!selectedItem) return null;

  const { categoryId, categoryName, item, aggregate } = selectedItem;
  const confidence = item.confidence == null ? '未送出' : item.confidence.toFixed(2);
  const trace = findTraceForItem(lastExtraction, categoryId, item);

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs space-y-3">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-gray-500 font-semibold">Item detail</div>
          <div className="font-mono text-[11px] text-gray-900 break-all">{item.itemKey}</div>
        </div>
        <span className={`shrink-0 rounded-full border px-2 py-0.5 font-semibold ${itemStatusClass(item)}`}>
          {itemStatusLabel(item)}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded border border-gray-200 bg-white px-2 py-0.5 text-gray-500 hover:bg-gray-100"
        >
          閉じる
        </button>
      </div>

      {item.reviewRequired && (
        <div className="rounded border border-rose-200 bg-rose-50 px-2 py-1 text-rose-700">
          reviewRequired: true / 変換時または fallback 時の人間確認対象です。
        </div>
      )}

      <dl className="rounded border border-gray-200 bg-white px-2">
        <DetailRow label="category" value={`${categoryId} / ${categoryName}`} />
        <DetailRow label="levelKey" value={item.level} />
        <DetailRow label="kind" value={formatDebugValue(item.kind, '未送出')} />
        <DetailRow label="sourceHeader" value={formatDebugValue(item.sourceHeader, '未送出')} />
        <DetailRow label="columnKey" value={item.columnKey} />
        <DetailRow label="dimension" value={item.dimension} />
        <DetailRow label="label" value={item.label} />
        <DetailRow label="expected" value={item.text} multiline />
        <DetailRow label="scoringPolicy" value={item.scoringPolicy} />
        <DetailRow label="groupKey" value={formatDebugValue(item.groupKey, 'なし')} />
        <DetailRow label="pairKey" value={formatDebugValue(item.pairKey, 'なし')} />
        <DetailRow label="pairedItemKey" value={formatDebugValue(item.pairedItemKey, 'なし')} />
        <DetailRow label="required" value={formatDebugValue(item.required)} />
        <DetailRow label="weight" value={formatDebugValue(item.weight, '未送出')} />
      </dl>

      <dl className="rounded border border-gray-200 bg-white px-2">
        <DetailRow label="filled" value={formatDebugValue(item.filled)} />
        <DetailRow label="value" value={formatDebugValue(item.value)} multiline />
        <DetailRow label="confidence" value={confidence} />
        <DetailRow label="wasProbed" value={formatDebugValue(item.wasProbed)} />
        <DetailRow label="reportedTurn" value={formatDebugValue(item.reportedTurn)} />
        <DetailRow label="sourceText" value={formatDebugValue(item.sourceText, '未送出')} multiline />
        {aggregate && (
          <DetailRow
            label="levelAggregate"
            value={`${aggregate.status} ${aggregate.filledCount}/${aggregate.requiredCount}`}
          />
        )}
      </dl>

      <div className="rounded border border-gray-200 bg-white px-2 py-1.5">
        <div className="mb-1 flex items-center gap-2">
          <span className="font-semibold text-gray-600">Extraction trace</span>
          {trace ? (
            <span className={`rounded-full border px-2 py-0.5 font-semibold ${traceStatusClass(trace)}`}>
              {trace.filled ? 'filled' : 'skipped'}
            </span>
          ) : (
            <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 font-semibold text-gray-500">
              no trace
            </span>
          )}
        </div>
        {trace ? (
          <dl>
            <DetailRow label="matched_by" value={traceResultLabel(trace)} />
            <DetailRow label="rawItemKey" value={formatDebugValue(trace.rawItem.item_key, '未送出')} />
            <DetailRow label="rawColumn" value={formatDebugValue(trace.rawItem.column_key, '未送出')} />
            <DetailRow label="rawDimension" value={formatDebugValue(trace.rawItem.dimension, '未送出')} />
            <DetailRow label="rawKind" value={formatDebugValue(trace.rawItem.kind, '未送出')} />
            <DetailRow label="rawValue" value={trace.value} multiline />
            <DetailRow label="rawSource" value={trace.sourceText} multiline />
          </dl>
        ) : (
          <div className="text-gray-500">
            直近の抽出結果ではこの item に紐付いた trace がありません。
          </div>
        )}
      </div>
    </div>
  );
}

function LastExtractionPanel({
  lastExtraction,
  itemSlots,
  onSelectItem,
}: {
  lastExtraction?: LastExtractionTrace;
  itemSlots?: DialogState['itemSlots'];
  onSelectItem: (categoryId: string, itemKey: string) => void;
}) {
  if (!lastExtraction) return null;

  const fills = lastExtraction.fills;
  const filledCount = fills.filter((trace) => trace.filled).length;

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs space-y-2">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-gray-600">Last extraction</span>
        <span className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-gray-600">
          {filledCount}/{lastExtraction.reportedItemCount} filled
        </span>
        <span className="rounded-full border border-gray-200 bg-white px-2 py-0.5 font-mono text-gray-600">
          {lastExtraction.extractionMs}ms
        </span>
      </div>

      <div className="rounded border border-gray-200 bg-white px-2 py-1.5">
        <div className="text-gray-500 font-semibold">student</div>
        <div className="mt-0.5 whitespace-pre-wrap break-words text-gray-900">
          {lastExtraction.studentUtterance || '未送出'}
        </div>
      </div>

      {fills.length > 0 ? (
        <div className="max-h-48 overflow-y-auto rounded border border-gray-200 bg-white">
          {fills.map((trace, index) => {
            const itemKey = trace.itemKey ?? trace.rawItem.item_key;
            const item = itemKey ? itemSlots?.[trace.categoryId]?.[itemKey] : undefined;
            const canSelect = Boolean(itemKey);
            return (
              <button
                key={`${trace.categoryId}-${trace.level}-${index}`}
                type="button"
                disabled={!canSelect}
                onClick={() => {
                  if (itemKey) onSelectItem(trace.categoryId, itemKey);
                }}
                className={`grid w-full grid-cols-[72px_112px_1fr] gap-2 border-b border-gray-100 px-2 py-1.5 text-left last:border-b-0 ${
                  canSelect ? 'hover:bg-cyan-50' : 'cursor-default'
                }`}
              >
                <span className={`self-start rounded-full border px-2 py-0.5 text-center font-semibold ${traceStatusClass(trace)}`}>
                  {trace.filled ? 'filled' : 'skip'}
                </span>
                <span className="min-w-0">
                  <span className="block font-mono text-[11px] text-gray-800">
                    {trace.categoryId}.{trace.level}
                  </span>
                  <span className="block truncate text-gray-500">
                    {item ? itemDisplayName(item) : itemKey ?? 'item 未割当'}
                  </span>
                </span>
                <span className="min-w-0">
                  <span className="block font-semibold text-gray-700">{traceResultLabel(trace)}</span>
                  <span className="block truncate text-gray-500">
                    {item?.pairKey ? `${item.pairKey} / ` : ''}{trace.value}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="rounded border border-gray-200 bg-white px-2 py-1.5 text-gray-500">
          reported_items は空です。
        </div>
      )}

      <details className="rounded border border-gray-200 bg-white">
        <summary className="cursor-pointer px-2 py-1.5 font-semibold text-gray-600">
          LLM raw response
        </summary>
        <pre className="max-h-48 overflow-auto border-t border-gray-100 px-2 py-1.5 text-[11px] text-gray-700">
          {formatJsonValue(lastExtraction.llmRawResponse)}
        </pre>
      </details>
    </div>
  );
}

export default function DentistFSMPanel({ fsmState, timing, targetCategories }: DentistFSMPanelProps) {
  const [selectedItemRef, setSelectedItemRef] = useState<SelectedItemRef | null>(null);
  const targetCategorySet = useMemo(() => buildTargetCategorySet(targetCategories), [targetCategories]);
  const selectedItem = useMemo<SelectedItemDetail | null>(() => {
    if (!fsmState || !selectedItemRef) return null;

    const item = fsmState.itemSlots?.[selectedItemRef.categoryId]?.[selectedItemRef.itemKey];
    if (!item) return null;

    return {
      categoryId: selectedItemRef.categoryId,
      categoryName: fsmState.slots?.[selectedItemRef.categoryId]?.category ?? selectedItemRef.categoryId,
      item,
      aggregate: fsmState.levelAggregates?.[selectedItemRef.categoryId]?.[item.level],
    };
  }, [fsmState, selectedItemRef]);

  if (!fsmState) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-3 text-xs w-full space-y-2">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-600">FSM状態</span>
          <span className="bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full text-xs">待機中</span>
        </div>
        <div className="text-gray-400">面接開始後に categoryDefinitions / itemSlots を表示します。</div>
      </div>
    );
  }

  const phase = PHASE_LABELS[fsmState.phase] ?? { label: fsmState.phase, color: 'bg-gray-100 text-gray-800' };
  const hasItemSlots = Object.values(fsmState.itemSlots ?? {})
    .some((categoryItems) => Object.keys(categoryItems).length > 0);
  const hasV31SourceCategories = Object.keys(fsmState.itemSlots ?? {})
    .some(isV31SourceCategoryId);
  const slots = Object.values(fsmState.slots ?? {}).filter((slot) => {
    if (!hasItemSlots) return true;
    if (slot.categoryId === 'C0') return true;
    if (hasV31SourceCategories && !isV31SourceCategoryId(slot.categoryId)) return false;
    return Object.keys(fsmState.itemSlots?.[slot.categoryId] ?? {}).length > 0;
  });
  const visibleCategoryIds = new Set(slots.map(slot => slot.categoryId));
  const naLevelMapAll = fsmState.naLevelMap ?? {};
  const naCategoriesAll = fsmState.naCategories ?? [];

  // 4色バー用の集計: NA / C0(緑) / 自発充填 / お助け充填 / 未取得
  let countNA = 0;
  let countSelf = 0;
  let countAssisted = 0;
  let countEmpty = 0;
  let countGreeting = 0;

  if (hasItemSlots) {
    for (const slot of slots) {
      const isC0 = slot.categoryId === 'C0';
      const items = Object.values(fsmState.itemSlots?.[slot.categoryId] ?? {});
      for (const item of items) {
        if (item.value === 'N/A') {
          countNA++;
        } else if (isReportedItem(item)) {
          if (isC0) {
            countGreeting++;
          } else if (item.wasProbed) {
            countAssisted++;
          } else {
            countSelf++;
          }
        } else {
          countEmpty++;
        }
      }
    }
  } else {
    for (const slot of slots) {
      const isFullNA = naCategoriesAll.includes(slot.categoryId);
      const naLevels = naLevelMapAll[slot.categoryId] ?? [];
      const isC0 = slot.categoryId === 'C0';
      for (const lvKey of getCategoryLevelKeys(fsmState, slot.categoryId)) {
        if (isFullNA || naLevels.includes(lvKey)) {
          countNA++;
        } else {
          const val = slot.filledLevels?.[lvKey];
          const isFilled = (val ?? '') !== '';
          if (isFilled) {
            if (isC0) {
              countGreeting++;
            } else if ((slot.probedLevels ?? []).includes(lvKey)) {
              countAssisted++;
            } else {
              countSelf++;
            }
          } else {
            countEmpty++;
          }
        }
      }
    }
  }
  const totalLevels = countNA + countSelf + countAssisted + countEmpty + countGreeting;
  const filledLevels = countSelf + countAssisted + countGreeting;
  const debugTotalItems = slots.reduce((sum, slot) => {
    return sum + Object.values(fsmState.itemSlots?.[slot.categoryId] ?? {})
      .filter((item) => item.value !== 'N/A')
      .length;
  }, 0);
  const debugFilledItems = slots.reduce((sum, slot) => {
    return sum + Object.values(fsmState.itemSlots?.[slot.categoryId] ?? {})
      .filter(isReportedItem)
      .length;
  }, 0);
  const kindSummaries = summarizeKinds(fsmState, visibleCategoryIds);
  const pairSummary = summarizePairs(fsmState, visibleCategoryIds);
  const progressDenominator = Math.max(totalLevels - countNA, 1);
  const barDenominator = Math.max(totalLevels, 1);

  // 現在確認中のスロット情報
  const currentSlot = fsmState.currentConfirmingCategory
    ? (fsmState.slots ?? {})[fsmState.currentConfirmingCategory]
    : null;
  const subPhaseLabel = fsmState.currentConfirmingSubPhase
    ? (SUB_PHASE_LABELS[fsmState.currentConfirmingSubPhase] ?? fsmState.currentConfirmingSubPhase)
    : null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 text-xs w-full space-y-2">
      {/* フェーズ + 今確認中のカテゴリ */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-gray-600">FSM状態</span>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${phase.color}`}>
          {phase.label}
        </span>
        {currentSlot && (
          <span className="flex items-center gap-1 text-yellow-700 font-medium bg-yellow-50 border border-yellow-200 rounded px-2 py-0.5">
            <span>{currentSlot.categoryId}: {currentSlot.category}</span>
            {subPhaseLabel && <span className="text-yellow-500 text-xs">（{subPhaseLabel}）</span>}
          </span>
        )}
        {fsmState.phase === 'listening' && (
          <span className="text-blue-500 text-xs">自由報告を聞いています</span>
        )}
        <span className="ml-auto text-gray-400">
          Categories: {slots.length}
          {debugTotalItems > 0 && ` / Items: ${debugFilledItems}/${debugTotalItems}`}
        </span>
      </div>

      {/* 進捗バー（4色: NA / 挨拶(緑) / 自発 / お助け） */}
      <div>
        <div className="flex justify-between text-gray-500 mb-1">
          <span>スロット充填</span>
          <span>{filledLevels}/{totalLevels - countNA} ({Math.round(filledLevels / progressDenominator * 100)}%)</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-1.5 flex overflow-hidden">
          {countNA > 0 && (
            <div className="bg-gray-400 h-1.5 transition-all" style={{ width: `${countNA / barDenominator * 100}%` }} />
          )}
          {countGreeting > 0 && (
            <div className="bg-green-500 h-1.5 transition-all" style={{ width: `${countGreeting / barDenominator * 100}%` }} />
          )}
          {countSelf > 0 && (
            <div className="bg-blue-500 h-1.5 transition-all" style={{ width: `${countSelf / barDenominator * 100}%` }} />
          )}
          {countAssisted > 0 && (
            <div className="bg-orange-400 h-1.5 transition-all" style={{ width: `${countAssisted / barDenominator * 100}%` }} />
          )}
        </div>
      </div>

      {kindSummaries.length > 0 && (
        <div className="flex flex-wrap gap-1 text-[11px] text-gray-500">
          {kindSummaries.map((summary) => (
            <span
              key={summary.key}
              className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5"
            >
              {summary.label} {summary.filled}/{summary.total}
            </span>
          ))}
          {pairSummary.total > 0 && (
            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-700">
              提案-受容ペア {pairSummary.filled}/{pairSummary.total}
            </span>
          )}
        </div>
      )}

      {/* カテゴリグリッド（横並び） */}
      <div className="flex flex-wrap gap-1">
        {slots.map(slot => {
          const categoryProgress = summarizeCategoryProgress(fsmState, slot.categoryId);
          const isCurrent = fsmState.currentConfirmingCategory === slot.categoryId;
          const isCoached = (fsmState.coachedCategoryIds ?? []).includes(slot.categoryId);
          const isNA = (fsmState.naCategories ?? []).includes(slot.categoryId);
          const isInstructionTarget = isInstructionTargetCategory(slot.categoryId, targetCategorySet);
          const isScoringScope = slot.categoryId !== 'C0'
            && !isNA
            && categoryProgress.total > 0
            && isInstructionTarget;
          const naLevels = (fsmState.naLevelMap ?? {})[slot.categoryId] ?? [];
          const levelKeys = getCategoryLevelKeys(fsmState, slot.categoryId).filter((levelKey) => {
            if (!hasItemSlots) return true;
            if (isNA || naLevels.includes(levelKey)) return false;
            return getLevelItems(fsmState, slot.categoryId, levelKey).length > 0;
          });
          const notes = (fsmState.conditionNotes ?? {})[slot.categoryId] ?? [];
          const aggregateSummary = Object.values(fsmState.levelAggregates?.[slot.categoryId] ?? {})
            .filter((aggregate) => aggregate.status === 'partial')
            .map((aggregate) => `${aggregate.level}:${aggregate.filledCount}/${aggregate.requiredCount}`)
            .join(', ');

          if (hasItemSlots) {
            return (
              <div
                key={slot.categoryId}
                title={`${slot.categoryId}: ${slot.category}\n充填: ${categoryProgress.filled}/${categoryProgress.total}${isScoringScope ? '\n[課題文の得点範囲]' : ''}${aggregateSummary ? `\n部分取得: ${aggregateSummary}` : ''}${slot.wasProbed ? ' [お助けあり]' : ''}${isCoached ? ' [コーチング完了]' : ''}${isNA ? ' [N/A]' : ''}${notes.length > 0 ? `\n---\n${notes.join('\n')}` : ''}`}
                className={`
                  flex w-full items-start gap-2 rounded border px-2 py-1.5
                  ${isNA ? 'border-gray-300 bg-gray-50' :
                    isCurrent ? `border-yellow-300 ${isScoringScope ? 'bg-emerald-50' : 'bg-yellow-50'} ring-2 ring-yellow-300` :
                    isScoringScope ? 'border-emerald-300 bg-emerald-50' :
                    isCoached ? 'border-green-300 bg-green-50' :
                    slot.filled ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white'}
                `}
              >
                <div className="w-28 shrink-0">
                  <div className={`font-mono text-xs font-semibold leading-tight ${isNA ? 'text-gray-800' : 'text-gray-700'}`}>
                    {slot.categoryId}
                  </div>
                  <div className="truncate text-[10px] leading-tight text-gray-500">{slot.category}</div>
                  {subPhaseLabel && isCurrent && (
                    <div className="truncate text-[10px] leading-tight text-yellow-600">{subPhaseLabel}</div>
                  )}
                  {isScoringScope && (
                    <div className="truncate text-[10px] font-semibold leading-tight text-emerald-700">得点範囲</div>
                  )}
                </div>
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
                  {levelKeys.map(lvKey => {
                    const val = slot.filledLevels?.[lvKey];
                    const isFilled = (val ?? '') !== '';
                    const levelStatus = getLevelStatus(fsmState, slot.categoryId, lvKey, isFilled, false);
                    const levelItems = getLevelItems(fsmState, slot.categoryId, lvKey);
                    const desc = fsmState.rubricDescriptions?.[slot.categoryId]?.[lvKey] ?? '';
                    const tableVal = fsmState.tableValues?.[slot.categoryId]?.[lvKey] ?? '';
                    const levelLabel = levelDisplayLabel(lvKey, levelItems);

                    return (
                      <div
                        key={lvKey}
                        className={`flex items-center gap-0.5 rounded border px-1 py-0.5 ${
                          isScoringScope ? 'border-emerald-100 bg-emerald-50/70' : 'border-gray-100 bg-white'
                        }`}
                      >
                        <span className="mr-0.5 min-w-[24px] text-[10px] font-semibold leading-none text-gray-500">
                          {levelLabel}
                        </span>
                        {levelItems.map((item, itemIndex) => {
                          const selected = selectedItemRef?.categoryId === slot.categoryId
                            && selectedItemRef.itemKey === item.itemKey;
                          return (
                            <span key={item.itemKey} className="relative inline-flex group">
                              <button
                                type="button"
                                aria-label={`${slot.categoryId} ${itemDisplayName(item)} item detail`}
                                onClick={() => setSelectedItemRef({ categoryId: slot.categoryId, itemKey: item.itemKey })}
                                className={`h-3 min-w-[14px] rounded-[3px] border px-0.5 text-center text-[9px] font-bold leading-[10px] ${
                                  itemDotClass(item)
                                } ${
                                  selected ? 'ring-2 ring-cyan-700 ring-offset-1' : 'hover:ring-2 hover:ring-cyan-500 hover:ring-offset-1'
                                } ${item.reviewRequired ? 'ring-1 ring-rose-400' : ''}`}
                              >
                                {itemChipLabel(item, itemIndex)}
                              </button>
                              <ItemSlotHoverTooltip
                                categoryId={slot.categoryId}
                                levelLabel={levelLabel}
                                levelStatus={levelStatusLabel(levelStatus)}
                                desc={desc}
                                tableVal={tableVal}
                                item={item}
                                notes={notes}
                              />
                            </span>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
                <div className="w-12 shrink-0 text-right text-[10px] font-semibold text-gray-500">
                  {categoryProgress.filled}/{categoryProgress.total}
                </div>
              </div>
            );
          }

          return (
            <div
              key={slot.categoryId}
              title={`${slot.categoryId}: ${slot.category}\n充填: ${categoryProgress.filled}/${categoryProgress.total}${isScoringScope ? '\n[課題文の得点範囲]' : ''}${aggregateSummary ? `\n部分取得: ${aggregateSummary}` : ''}${slot.wasProbed ? ' [お助けあり]' : ''}${isCoached ? ' [コーチング完了]' : ''}${isNA ? ' [N/A]' : ''}${notes.length > 0 ? `\n---\n${notes.join('\n')}` : ''}`}
              className={`
                flex flex-col items-center rounded p-1 cursor-default min-w-[36px]
                ${isNA ? 'border border-gray-300 bg-gray-50' :
                  isCurrent ? `ring-2 ring-yellow-400 ${isScoringScope ? 'bg-emerald-50 border border-emerald-300' : 'bg-yellow-50'}` :
                  isScoringScope ? 'bg-emerald-50 border border-emerald-300' :
                  isCoached ? 'bg-green-50 border border-green-300' :
                  'border border-transparent'}
              `}
            >
              <span className={`leading-none mb-0.5 text-xs ${isNA ? 'text-gray-800 font-medium' : 'text-gray-500'}`}>{slot.categoryId}</span>
              <div className="flex gap-0.5">
                {levelKeys.map(lvKey => {
                  const val = slot.filledLevels?.[lvKey];
                  const isFilled = (val ?? '') !== '';
                  const isLevelUnavailable = naLevels.includes(lvKey);
                  const levelStatus = getLevelStatus(fsmState, slot.categoryId, lvKey, isFilled, isNA || isLevelUnavailable);
                  const levelItems = getLevelItems(fsmState, slot.categoryId, lvKey);
                  const desc = fsmState.rubricDescriptions?.[slot.categoryId]?.[lvKey] ?? '';
                  const status = isNA
                    ? 'カテゴリN/A'
                    : isLevelUnavailable
                      ? 'レベル未定義'
                      : levelStatusLabel(levelStatus);
                  const tableVal = fsmState.tableValues?.[slot.categoryId]?.[lvKey] ?? '';
                  const defaultSelectedItem = levelItems.find(isReportedItem) ?? levelItems[0] ?? null;
                  const levelLabel = levelDisplayLabel(lvKey, levelItems);
                  if (!defaultSelectedItem && hasItemSlots) return null;
                  return (
                    <div key={lvKey} className="flex flex-col items-center gap-0.5">
                      <span className="max-w-[28px] truncate text-[9px] leading-none text-gray-400">
                        {levelLabel}
                      </span>
                      <div className="flex max-w-[76px] flex-wrap justify-center gap-0.5">
                        {levelItems.map((item, itemIndex) => {
                          const selected = selectedItemRef?.categoryId === slot.categoryId
                            && selectedItemRef.itemKey === item.itemKey;
                          return (
                            <span key={item.itemKey} className="relative inline-flex group">
                              <button
                                type="button"
                                aria-label={`${slot.categoryId} ${itemDisplayName(item)} item detail`}
                                onClick={() => setSelectedItemRef({ categoryId: slot.categoryId, itemKey: item.itemKey })}
                                className={`h-3 min-w-[14px] rounded-[3px] border px-0.5 text-center text-[9px] font-bold leading-[10px] ${
                                  itemDotClass(item)
                                } ${
                                  selected ? 'ring-2 ring-cyan-700 ring-offset-1' : 'hover:ring-2 hover:ring-cyan-500 hover:ring-offset-1'
                                } ${item.reviewRequired ? 'ring-1 ring-rose-400' : ''}`}
                              >
                                {itemChipLabel(item, itemIndex)}
                              </button>
                              <ItemSlotHoverTooltip
                                categoryId={slot.categoryId}
                                levelLabel={levelLabel}
                                levelStatus={status}
                                desc={desc}
                                tableVal={tableVal}
                                item={item}
                                notes={notes}
                                align="center"
                              />
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <ItemSlotDetailPanel
        selectedItem={selectedItem}
        lastExtraction={fsmState.debugTrace?.lastExtraction}
        onClose={() => setSelectedItemRef(null)}
      />

      <LastExtractionPanel
        lastExtraction={fsmState.debugTrace?.lastExtraction}
        itemSlots={fsmState.itemSlots}
        onSelectItem={(categoryId, itemKey) => setSelectedItemRef({ categoryId, itemKey })}
      />

      {/* 凡例 */}
      <div className="flex items-center gap-3 text-gray-400">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-green-500 inline-block"/>挨拶</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-blue-500 inline-block"/>自発</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-orange-400 inline-block"/>お助け</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-100 border border-amber-500 inline-block"/>部分</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border border-emerald-300 bg-emerald-50 inline-block"/>課題文の得点範囲</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm ring-2 ring-yellow-400 inline-block"/>確認中</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-green-50 border border-green-300 inline-block"/>完了</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-gray-500 inline-block"/>レベル未定義</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-gray-800 inline-block"/>N/A</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-gray-300 inline-block"/>未取得</span>
      </div>

      {/* ターン数 */}
      <div className="flex items-center gap-4 text-gray-400">
        <span>ターン: {fsmState.turnCount}</span>
        <span className="text-orange-400">空振り: {fsmState.dryTurns ?? 0}</span>
        {fsmState.phase === 'listening' && fsmState.dryTurnThreshold != null && (
          <span className="text-yellow-500">お助けまで: {Math.max(0, fsmState.dryTurnThreshold - (fsmState.dryTurns ?? 0))}</span>
        )}
      </div>

      {/* タイミング */}
      {timing?.total_ms !== undefined && (
        <div className="border-t pt-2 flex flex-wrap gap-x-4 gap-y-0.5 text-gray-500">
          <span className="w-full font-semibold text-gray-600 mb-0.5">⏱️ 応答時間</span>
          <span>合計 <span className="font-mono text-gray-700">{timing.total_ms}ms</span></span>
          {timing.extraction_ms !== undefined && (
            <span className="text-gray-400">抽出 <span className="font-mono">{timing.extraction_ms}ms</span></span>
          )}
          {timing.nlg_ms !== undefined && (
            <span className="text-gray-400">NLG <span className="font-mono">{timing.nlg_ms}ms</span></span>
          )}
          {timing.tts_lipsync_ms !== undefined && (
            <span className="text-gray-400">TTS <span className="font-mono">{timing.tts_lipsync_ms}ms</span></span>
          )}
        </div>
      )}
    </div>
  );
}
